import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import React, { useRef } from 'react';
import { useFloating, autoUpdate, offset, flip, useTransitionStyles, useHover, safePolygon, useFocus, useClick, useDismiss, useRole, useInteractions, FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import classnames from 'classnames';
import { useConfigContextLoose } from '@cognite/cogs-core';
import { getAppendToLocal, getShouldAppendToParent } from './util.js';

/**
 * Dropdown component is a wrapper around the floating component to provide a dropdown UI.
 */
var Dropdown = /*#__PURE__*/React.forwardRef(function (_a, outerRef) {
  var _b;
  var appendTo = _a.appendTo,
    children = _a.children,
    className = _a.className,
    _a$openOnClick = _a.openOnClick,
    openOnClick = _a$openOnClick === undefined ? true : _a$openOnClick,
    openOnHover = _a.openOnHover,
    openOnFocus = _a.openOnFocus,
    openAsContextMenu = _a.openAsContextMenu,
    disabled = _a.disabled,
    visible = _a.visible,
    content = _a.content,
    contentContainerProps = _a.contentContainerProps,
    offset$1 = _a.offset,
    onShow = _a.onShow,
    onHide = _a.onHide,
    onClickOutside = _a.onClickOutside,
    _a$hideOnOutsideClick = _a.hideOnOutsideClick,
    hideOnOutsideClick = _a$hideOnOutsideClick === undefined ? true : _a$hideOnOutsideClick,
    _a$role = _a.role,
    roleAttribute = _a$role === undefined ? 'dialog' : _a$role,
    _a$placement = _a.placement,
    placement = _a$placement === undefined ? 'bottom-start' : _a$placement,
    _a$disableAdaptivePla = _a.disableAdaptivePlacement,
    disableAdaptivePlacement = _a$disableAdaptivePla === undefined ? false : _a$disableAdaptivePla,
    _a$disableFocusTrap = _a.disableFocusTrap,
    disableFocusTrap = _a$disableFocusTrap === undefined ? false : _a$disableFocusTrap,
    floatingUiOptions = _a.floatingUiOptions,
    floatingPortalProps = _a.floatingPortalProps,
    triggerContainerProps = _a.triggerContainerProps,
    floatingFocusManagerProps = _a.floatingFocusManagerProps,
    otherProps = __rest(_a, ["appendTo", "children", "className", "openOnClick", "openOnHover", "openOnFocus", "openAsContextMenu", "disabled", "visible", "content", "contentContainerProps", "offset", "onShow", "onHide", "onClickOutside", "hideOnOutsideClick", "role", "placement", "disableAdaptivePlacement", "disableFocusTrap", "floatingUiOptions", "floatingPortalProps", "triggerContainerProps", "floatingFocusManagerProps"]);
  var innerRef = useRef(null);
  var ref = outerRef || innerRef;
  var _React$useState = React.useState(!!visible),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    show = _React$useState2[0],
    setShow = _React$useState2[1];
  var _useConfigContextLoos = useConfigContextLoose(),
    getOverlayContainer = _useConfigContextLoos.getOverlayContainer;
  var appendToLocal = getAppendToLocal({
    appendTo: appendTo,
    getOverlayContainer: getOverlayContainer,
    ref: ref
  });
  var shouldAppendToParent = getShouldAppendToParent({
    appendTo: appendTo,
    getOverlayContainer: getOverlayContainer
  });
  var _useFloating = useFloating(Object.assign(Object.assign({
      open: visible !== null && visible !== undefined ? visible : show,
      onOpenChange: function onOpenChange(open) {
        if (open) onShow === null || onShow === undefined ? undefined : onShow(open);
        if (!open) onHide === null || onHide === undefined ? undefined : onHide(open);
        setShow(open);
      },
      placement: placement,
      transform: true,
      strategy: shouldAppendToParent ? 'absolute' : 'fixed',
      whileElementsMounted: autoUpdate
    }, floatingUiOptions), {
      middleware: [offset(offset$1), disableAdaptivePlacement ? undefined : flip()].concat(_toConsumableArray((_b = floatingUiOptions === null || floatingUiOptions === undefined ? undefined : floatingUiOptions.middleware) !== null && _b !== undefined ? _b : []))
    })),
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles,
    context = _useFloating.context;
  var _useTransitionStyles = useTransitionStyles(context),
    isMounted = _useTransitionStyles.isMounted,
    transitionStyles = _useTransitionStyles.styles;
  var hover = useHover(context, {
    enabled: !!openOnHover,
    handleClose: safePolygon()
  });
  var focus = useFocus(context, {
    enabled: !!openOnFocus
  });
  var click = useClick(context, {
    enabled: !!openOnClick
  });
  var dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: function outsidePress(e) {
      onClickOutside === null || onClickOutside === undefined ? undefined : onClickOutside(e);
      return hideOnOutsideClick;
    }
  });
  var role = useRole(context, {
    role: roleAttribute
  });
  var _useInteractions = useInteractions([click, hover, focus, dismiss, role]),
    getReferenceProps = _useInteractions.getReferenceProps,
    getFloatingProps = _useInteractions.getFloatingProps;
  var _c = triggerContainerProps !== null && triggerContainerProps !== undefined ? triggerContainerProps : {},
    triggerContainerClassName = _c.className,
    triggerContainerRest = __rest(_c, ["className"]);
  var renderedContent = isMounted && (visible !== null && visible !== undefined ? visible : show) && jsx(FloatingFocusManager, Object.assign({
    context: context,
    disabled: disableFocusTrap
  }, floatingFocusManagerProps, {
    children: jsx("div", Object.assign({}, contentContainerProps, getFloatingProps(), {
      className: classnames('cogs-lab', 'cogs-dropdown__content', contentContainerProps === null || contentContainerProps === undefined ? undefined : contentContainerProps.className),
      "data-dropdown-placement": placement,
      ref: refs.setFloating,
      style: Object.assign(Object.assign(Object.assign({}, floatingStyles), transitionStyles), contentContainerProps === null || contentContainerProps === undefined ? undefined : contentContainerProps.style),
      children: jsx("div", {
        children: content
      })
    }))
  }));
  if (disabled) return children;
  return jsxs("div", Object.assign({
    className: classnames('cogs-lab', 'cogs-dropdown', className)
  }, otherProps, {
    ref: ref,
    children: [jsx("div", Object.assign({
      className: classnames('cogs-lab', 'cogs-dropdown__anchor-el', triggerContainerClassName),
      ref: refs.setReference,
      onContextMenu: function onContextMenu(e) {
        if (!openAsContextMenu) return;
        e.preventDefault();
        setShow(true);
      },
      role: "button",
      "aria-expanded": isMounted && (visible !== null && visible !== undefined ? visible : show)
    }, getReferenceProps(triggerContainerRest), {
      children: children
    })), appendToLocal ? jsx(FloatingPortal, Object.assign({
      root: appendToLocal
    }, floatingPortalProps, {
      children: renderedContent
    })) : renderedContent]
  }));
});

export { Dropdown };
